import React, {useMemo} from "react";

const InlineGrammarChecker = ({text, errors, onApplySuggestion}) => {
	// Memoize the parsed text parts
	const textParts = useMemo(() => {
		if (!text || !errors || errors.length === 0) {
			return [{type: "normal", text: text || "", key: "normal-0"}];
		}

		// Sort errors by their position in the text
		const errorPositions = [];
		
		errors.forEach((err, errIndex) => {
			const contextText = err.context?.text || "";
			const offset = err.offset || 0;
			const length = err.length || 0;
			const suggestion = err.replacements && err.replacements.length > 0 ? err.replacements[0].value : "";
			const errorText = contextText.substring(offset, offset + length);

			if (!errorText || !suggestion) return;

			// Try to find the error text in the main text
			let errorStartIndex = -1;
			let errorEndIndex = -1;

			// First, try to find the context text in the main text
			const contextIndex = text.indexOf(contextText);
			if (contextIndex !== -1) {
				errorStartIndex = contextIndex + offset;
				errorEndIndex = errorStartIndex + length;
			} else {
				// Fallback: search for the error text directly
				// Search from the beginning, but try to avoid overlapping with previous errors
				let searchStart = 0;
				const errorIndex = text.indexOf(errorText, searchStart);
				if (errorIndex !== -1) {
					errorStartIndex = errorIndex;
					errorEndIndex = errorIndex + errorText.length;
				}
			}

			if (errorStartIndex !== -1 && errorEndIndex <= text.length) {
				errorPositions.push({
					startIndex: errorStartIndex,
					endIndex: errorEndIndex,
					errorText: errorText,
					suggestion: suggestion,
					errorIndex: errIndex,
				});
			}
		});

		// Sort by start index
		errorPositions.sort((a, b) => a.startIndex - b.startIndex);

		// Remove overlapping errors (keep the first one)
		const filteredPositions = [];
		let lastEndIndex = 0;

		errorPositions.forEach((pos) => {
			if (pos.startIndex >= lastEndIndex) {
				filteredPositions.push(pos);
				lastEndIndex = pos.endIndex;
			}
		});

		// Build parts array
		const parts = [];
		let currentIndex = 0;

		filteredPositions.forEach((pos, idx) => {
			// Add normal text before error
			if (pos.startIndex > currentIndex) {
				parts.push({
					type: "normal",
					text: text.substring(currentIndex, pos.startIndex),
					key: `normal-${currentIndex}-${idx}`,
				});
			}

			// Add error text
			parts.push({
				type: "error",
				text: pos.errorText,
				suggestion: pos.suggestion,
				errorIndex: pos.errorIndex,
				startIndex: pos.startIndex,
				endIndex: pos.endIndex,
				key: `error-${pos.errorIndex}-${idx}`,
			});

			currentIndex = pos.endIndex;
		});

		// Add remaining text
		if (currentIndex < text.length) {
			parts.push({
				type: "normal",
				text: text.substring(currentIndex),
				key: `normal-${currentIndex}-end`,
			});
		}

		return parts.length > 0 ? parts : [{type: "normal", text: text, key: "normal-0"}];
	}, [text, errors]);

	const handleErrorClick = (part) => {
		if (!part.suggestion || part.startIndex === undefined || part.endIndex === undefined) return;

		const newText = text.substring(0, part.startIndex) + part.suggestion + text.substring(part.endIndex);
		onApplySuggestion(newText, part.errorIndex);
	};

	return (
		<div className="inline-grammar-checker">
			<div className="inline-grammar-text">
				{textParts.map((part) => {
					if (part.type === "error") {
						return (
							<span key={part.key} className="inline-grammar-wrapper">
								<span 
									className="inline-grammar-error" 
									onClick={() => handleErrorClick(part)} 
									title={`Click to replace "${part.text}" with "${part.suggestion}"`}
								>
									{part.text}
								</span>
								{part.suggestion && (
									<span className="inline-grammar-suggestion" title="Suggested correction">
										{part.suggestion}
									</span>
								)}
							</span>
						);
					}
					return <span key={part.key}>{part.text}</span>;
				})}
			</div>
		</div>
	);
};

export default InlineGrammarChecker;

